import { useCreateWorkspaceMutation } from '@graphql/mutations/workspace/create.graphql';
import {
  useCreateCheckoutSessionMutation,
  useCreateResubscribeCheckoutSessionMutation,
} from '@graphql/mutations/billing_subscription/core.graphql';

import { usePrices } from '@hooks/billing-subscription';
import { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import { cookies } from '@modules/auth/cookies';
import { useCallback } from 'react';

type StartCheckoutCommonArgs = {
  /**
   * The checkout's workspace ID. If `createWorkspace` is true this can be `undefined`.
   */
  workspaceId?: string;
  /**
   * Should a workspace be created if one does not exist?
   */
  createWorkspace?: boolean;
  /**
   * The quantity to purchase.
   */
  seats?: number;
  promotionalCode?: string;
  successUrl?: string;
  cancelUrl?: string;
};

type StartCheckoutByIntervalAndTypeArgs = StartCheckoutCommonArgs & {
  priceId?: never;
  interval: 'month' | 'year';
  type: 'INDIVIDUAL' | 'TEAM';
};

type StartCheckoutByPriceIdArgs = StartCheckoutCommonArgs & {
  /**
   * The Blinq price ID (not Stripe price ID).
   */
  priceId: string;
  interval?: never;
  type?: never;
};

type StartCheckoutArgs = StartCheckoutByIntervalAndTypeArgs | StartCheckoutByPriceIdArgs;

interface UseCheckout {
  /**
   * Re-directs to a Stripe checkout session returning to the success or cancel URL. If `createWorkspace`
   * is true and `workspaceId` is not provided, a workspace will be created according to the `priceId`.
   * @param args The checkout arguments.
   * @returns Re-directs to a Stripe checkout session.
   */
  startCheckout: (args: StartCheckoutArgs) => Promise<void>;
  /**
   * Re-directs to a Stripe resubscribe checkout session returning to the success or cancel URL. This is for
   * customers resubscribing after cancelling. It will never create a workspace or new Stripe customer.
   * @param args The checkout arguments.
   * @returns Re-directs to a Stripe checkout session.
   */
  startResubscribeCheckout: (args: StartCheckoutArgs) => Promise<void>;
  loading: boolean;
  pricesLoading: boolean;
}

function useCheckout(): UseCheckout {
  const { prices, loading: pricesLoading } = usePrices();
  const [createCheckoutSession, { loading: checkoutLoading }] = useCreateCheckoutSessionMutation();
  const [createResubscribeCheckoutSession, { loading: resubscribeCheckoutLoading }] =
    useCreateResubscribeCheckoutSessionMutation();
  const [createWorkspaceMutation, { loading: creatingWorkspace }] = useCreateWorkspaceMutation();

  const startCheckout = useCallback(
    async ({
      workspaceId,
      seats,
      promotionalCode,
      successUrl,
      cancelUrl,
      createWorkspace = false,
      ...pricingArgs
    }: StartCheckoutArgs) => {
      let price: BillingSubscriptionPriceDetailsFragment | undefined;

      if (pricingArgs.priceId) {
        price = prices?.find((p) => p.id === pricingArgs.priceId);
      } else if (pricingArgs.interval && pricingArgs.type) {
        const { interval, type } = pricingArgs;
        price = prices?.find((p) => p.interval === interval && p.product?.type === type);
      }

      if (!price) {
        throw new Error(`Failed to find price.`);
      }

      if (!price.product?.type) {
        throw new Error(`Failed to find product type for price "${price.id}".`);
      }

      if (!workspaceId && createWorkspace) {
        const { data } = await createWorkspaceMutation({
          variables: {
            createInput: {
              type: price.product.type,
            },
          },
        });

        workspaceId = data?.createWorkspace?.workspace.id;
      }

      if (!workspaceId) {
        throw new Error(`Cannot start checkout. No workspace ID provided.`);
      }

      const defaultReturnUrl = window.location.href;

      const { data } = await createCheckoutSession({
        variables: {
          input: {
            subscription_price_id: price.id,
            workspace_id: workspaceId,
            success_url: successUrl ?? defaultReturnUrl,
            cancel_url: cancelUrl ?? defaultReturnUrl,
            promotion_code: promotionalCode,
            seats,
            ps_xid: cookies.get('ps_xid'),
            ps_partner_key: cookies.get('ps_partner_key'),
          },
        },
      });

      const checkoutUrl = data?.createCheckoutSession?.checkout_url;

      if (!checkoutUrl) {
        throw new Error(`Failed to create checkout session.`);
      }

      window.location.assign(checkoutUrl);
    },
    [createCheckoutSession, createWorkspaceMutation, prices]
  );

  const startResubscribeCheckout = useCallback(
    async ({
      workspaceId,
      seats,
      promotionalCode,
      successUrl,
      cancelUrl,
      ...pricingArgs
    }: StartCheckoutArgs) => {
      let price: BillingSubscriptionPriceDetailsFragment | undefined;

      if (pricingArgs.priceId) {
        price = prices?.find((p) => p.id === pricingArgs.priceId);
      } else if (pricingArgs.interval && pricingArgs.type) {
        const { interval, type } = pricingArgs;
        price = prices?.find((p) => p.interval === interval && p.product?.type === type);
      }

      if (!price) {
        throw new Error(`Failed to find price.`);
      }

      if (!workspaceId) {
        throw new Error(`Cannot start resubscribe checkout. No workspace ID provided.`);
      }

      const defaultReturnUrl = window.location.href;

      const { data } = await createResubscribeCheckoutSession({
        variables: {
          input: {
            subscription_price_id: price.id,
            workspace_id: workspaceId,
            success_url: successUrl ?? defaultReturnUrl,
            cancel_url: cancelUrl ?? defaultReturnUrl,
            promotion_code: promotionalCode,
            seats,
          },
        },
      });

      const checkoutUrl = data?.createResubscribeCheckoutSession?.checkout_url;

      if (!checkoutUrl) {
        throw new Error(`Failed to create checkout session.`);
      }

      window.location.assign(checkoutUrl);
    },
    [createResubscribeCheckoutSession, prices]
  );

  return {
    startCheckout,
    startResubscribeCheckout,
    loading: checkoutLoading || resubscribeCheckoutLoading || creatingWorkspace,
    pricesLoading,
  };
}

export { useCheckout, type StartCheckoutArgs };
